import { Suspense, lazy, useCallback, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MEASUREMENT_SKETCH_MEDIA_ENTITY_TYPE,
  type MeasurementSketchRef,
} from '@tailoring-crm/shared';
import { useApiClient } from '../../lib/api/api-context.js';
import { uploadSketchRendering } from '../../lib/design-board/sketch-export.js';
import {
  createMeasurementSketch,
  useMeasurementSketch,
} from '../../lib/measurements/sketches-api.js';
import type { SketchSnapshot } from '../design-board/design-sketch-canvas.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';

/**
 * THE DRAWING THAT GOES WITH A SET OF MEASUREMENTS — opened full-screen from the capture form,
 * the garment-type editor, or an order's garment.
 *
 * It saves a NEW sketch every time and hands its reference back; it never writes the profile,
 * template or order item itself. The caller hangs the drawing on its owner with its own save,
 * which is what keeps "redraw for this order" from ever reaching the customer's profile, and a
 * cancelled capture form from leaving a half-changed profile behind.
 *
 * The canvas is the design board's (`design-sketch-canvas.tsx`, lazily loaded — the only module
 * that imports the drawing tool). When there is no drawing yet it opens on the garment type's
 * outline, locked, for the tailor to draw over.
 */

const DesignSketchCanvas = lazy(() => import('../design-board/design-sketch-canvas.js'));

export interface MeasurementSketchDialogProps {
  garmentTypeKey: string;
  /** For the heading: "Priya — blouse", "Blouse outline". */
  name: string;
  /** The drawing to open with, or null for a fresh sheet. */
  sketchId: string | null;
  /** The outline a fresh sheet starts from (locked). Ignored when `sketchId` is given. */
  outlineSketchId?: string | null;
  onSaved: (sketch: MeasurementSketchRef) => void;
  onClose: () => void;
}

export function MeasurementSketchDialog({
  garmentTypeKey,
  name,
  sketchId,
  outlineSketchId = null,
  onSaved,
  onClose,
}: MeasurementSketchDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const client = useApiClient();

  const existing = useMeasurementSketch(sketchId);
  const outline = useMeasurementSketch(sketchId === null ? outlineSketchId : null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const takeSnapshot = useRef<(() => Promise<SketchSnapshot>) | null>(null);
  const bindCanvas = useCallback((take: () => Promise<SketchSnapshot>) => {
    takeSnapshot.current = take;
  }, []);

  /**
   * Snapshot, then the picture, then the drawing — the design board's order, for its reason: the
   * scene is what cannot be redrawn, so a picture that fails to upload is swallowed and the
   * drawing saves without one rather than not at all.
   */
  const submit = async (): Promise<void> => {
    const take = takeSnapshot.current;
    if (!take) return;

    setSaving(true);
    setError(null);
    try {
      const snapshot = await take();

      let previewMediaId: string | null = null;
      if (snapshot.png) {
        try {
          previewMediaId = await uploadSketchRendering({
            client,
            entityType: MEASUREMENT_SKETCH_MEDIA_ENTITY_TYPE,
            png: snapshot.png,
            title: name,
          });
        } catch {
          previewMediaId = null;
        }
      }

      const saved = await createMeasurementSketch(client, {
        garmentTypeKey,
        scene: snapshot.scene,
        previewMediaId,
      });

      toast({ title: t('measurementSketch.saved') });
      onSaved({ sketchId: saved.id, previewMediaId: saved.previewMediaId });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    } finally {
      setSaving(false);
    }
  };

  /*
   * Both the drawing and the outline are fetched BEFORE the canvas mounts: the editor reads its
   * starting scene exactly once, and a scene that arrived a moment late would open a blank sheet
   * that the first Save then writes as the drawing. A failed OUTLINE is not worth blocking on —
   * the tailor gets a blank sheet — but a failed drawing is, since saving would replace it.
   */
  const loading =
    (sketchId !== null && existing.isPending) || (outline.isFetching && !outline.data);
  const failed = sketchId !== null && existing.isError;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        /* Nearly the whole viewport: a drawing surface the size of a form field is not one. */
        className="flex h-[92vh] max-h-[92vh] w-[96vw] max-w-[96vw] flex-col gap-3 overflow-hidden sm:max-w-[96vw]"
        /*
         * Escape and a press outside do NOT close a drawing. Escape is how the canvas finishes a
         * text label or drops a selection, and both used to throw the whole unsaved drawing away.
         * Cancel and the close button are the ways out.
         */
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('measurementSketch.title', { name })}</DialogTitle>
        </DialogHeader>

        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
          >
            {error}
          </p>
        ) : null}

        {failed ? (
          <p role="alert" className="text-sm text-destructive">
            {t('measurementSketch.loadFailed')}
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-border">
          {loading || failed ? (
            <div className="p-4">
              <ListSkeleton rows={6} />
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="p-4">
                  <ListSkeleton rows={6} />
                </div>
              }
            >
              <DesignSketchCanvas
                initialScene={existing.data?.scene ?? null}
                lockedSeed={outline.data?.scene ?? null}
                readOnly={false}
                onReady={bindCanvas}
              />
            </Suspense>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={loading || failed || saving}
            onClick={() => void submit()}
          >
            {saving ? t('measurementSketch.saving') : t('measurementSketch.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
